import { MailOutline, Edit, Check } from '@mui/icons-material';
import { Stack, Typography, TextField, IconButton, Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import ABTestLabelBox from 'domains/campaigns/components/ABTestLabelBox';
import {
    setTemplateId,
    setPreHeader,
    setSenderProfileId,
    setSubjectLine,
    setSendName,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignChannels, SplitTypes, ABTestType } from 'domains/campaigns/types';
import { CampaignStatus } from 'models/enums';
import useQueryParams from 'hooks/useQueryParams';
import ContentSelector from 'domains/campaigns/components/CustomCampaign/components/ContentSelector';
import FormLayout from 'domains/core/components/FormLayout';
import useTemplates from 'hooks/queries/useTemplates';
import { hasCompleteEmailContent, areEmailContentsIdentical, isReservedSendName } from '../../utils';
import AdditionalContentFields from './AdditionalContentFields';

// Constants for guiding text and badge content
export const GUIDING_TEXT = {
    EMAIL_A:
        'Focus on the goal of the customer interaction and choose content from the dropdown that supports your objective.',
    EMAIL_B:
        "Make one focused change to your original content. Choose what you'd like to test—such as subject line, CTA, or layout. For best insights, change just one thing.",
    FIRST_EMAIL:
        'Select the first email you want your audience to receive. Keep the goal of your Curation in mind and choose content that best supports your objective.',
    SUBSEQUENT_EMAIL:
        'Focus on the goal of the customer interaction and choose content from the dropdown that supports your objective.',
} as const;

export const BADGE_CONTENT = {
    EMAIL_A: { text: 'Content A', badgeLabel: 'A' },
    EMAIL_B: { text: 'Content B', badgeLabel: 'B' },
} as const;

export const HEADER_TEXT = {
    SELECT_CONTENT: 'select content',
} as const;

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    dispatchCampaignAction: React.Dispatch<any>;
    setContentPreviewId: (id: number) => void;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const EmailContentStep = ({
    activeSendIndex,
    campaignState,
    contentPreviewId,
    dispatchCampaignAction,
    setContentPreviewId,
    setIsContinueEnabled,
    setNextStep,
    onContentUnavailableChange,
}: Props) => {
    const { editLiveCurationsFeature: hasEditLiveCurationsFeatureFlag } = useFlags();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const isEditingPausedCampaign = hasEditLiveCurationsFeatureFlag && status === CampaignStatus.PAUSED;

    // Check if editing an existing draft or paused campaign (status param exists means editing existing)
    const isEditingExistingDraftOrPaused = status === CampaignStatus.DRAFT || status === CampaignStatus.PAUSED;

    const currentSend = campaignState.sends[activeSendIndex];
    const currentSendIsEmail = currentSend?.channel === CampaignChannels.EMAIL;
    const { preHeader, senderProfileId, subjectLine, templateId, name: sendName } = currentSendIsEmail && currentSend;

    // A/B Testing logic
    const isABTestingContent = campaignState.abTestConfig?.testOn === ABTestType.CONTENT;
    const isABTestingSendTime = campaignState.abTestConfig?.testOn === ABTestType.SEND_TIME;
    const isABTesting = isABTestingContent || isABTestingSendTime;

    // Skip send name updates when editing existing draft/paused curations (non-A/B testing)
    const shouldSkipSendNameUpdate = isEditingExistingDraftOrPaused && !isABTesting;
    const isEmailA = (isABTestingContent || isABTestingSendTime) && activeSendIndex === 0;
    const isEmailB = (isABTestingContent || isABTestingSendTime) && activeSendIndex === 1;

    // Edit sendname feature is enabled only for A/B testing flows
    const editSendnameEnabled = isABTestingContent || isABTestingSendTime;

    const { data: templates = [], isLoading: isLoadingTemplates } = useTemplates();

    // State for editable email names
    const [isEditingName, setIsEditingName] = useState(false);
    const curationName = campaignState.name || '';

    // Get template name from the selected template
    const selectedTemplate = templates.find((template) => template.id === templateId);
    const templateName = selectedTemplate?.name || '';

    // Generate the default name based on A/B testing type and send index
    const generateDefaultName = (sendIndex: number = activeSendIndex) => {
        const curationPart = curationName || '';

        // A/B Testing: Use specific naming convention
        if (isABTestingContent) {
            const suffix = sendIndex === 0 ? 'Content A' : 'Content B';
            return `${curationPart} - ${suffix}`;
        }

        if (isABTestingSendTime) {
            const suffix = sendIndex === 0 ? 'Sendtime A' : 'Sendtime B';
            return `${curationPart} - ${suffix}`;
        }

        // Non-A/B testing: Use original format with template name and UUID
        const uuid = uuidv4();
        const templatePart = templateName || '';
        return `${curationPart} - ${templatePart}.${uuid}`;
    };

    // Get the display name: use sendName from campaignState if available, otherwise use default
    const getDisplayName = () => {
        if (sendName && sendName.trim() !== '') {
            return sendName;
        }
        return generateDefaultName();
    };
    const [customName, setCustomName] = useState<string>(getDisplayName());

    // Validation: Check if name is duplicate or reserved
    const isDuplicateName = (() => {
        if (customName.trim() === '') return false;
        // Check if any other send has the same name
        return campaignState.sends.some((send, index) => index !== activeSendIndex && send.name === customName);
    })();
    const isReservedName = isABTestingContent && isReservedSendName(customName);
    const hasNameError = isDuplicateName || isReservedName;
    const nameErrorMessage = isDuplicateName
        ? 'Both send names must be different'
        : isReservedName
        ? 'Please choose a custom name for your send'
        : '';

    // Sync local state with campaignState when sendName changes
    useEffect(() => {
        setCustomName(getDisplayName());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendName, activeSendIndex]);

    // Initialize send name in campaignState if it's empty
    // Skip for existing draft/paused curations to preserve existing names
    useEffect(() => {
        if (shouldSkipSendNameUpdate) return;

        if (!sendName || sendName.trim() === '') {
            const defaultName = generateDefaultName();
            dispatchCampaignAction(setSendName(defaultName, activeSendIndex));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex, curationName, shouldSkipSendNameUpdate]);

    //This is for updated sendname along with change in content or templateName
    //This doesn't apply to A/B Testing since we are not using templateName in it's sendName

    useEffect(() => {
        if (shouldSkipSendNameUpdate || isABTesting) return;

        if (!sendName || sendName.trim() === '') {
            const defaultName = generateDefaultName();
            dispatchCampaignAction(setSendName(defaultName, activeSendIndex));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isABTestingContent, activeSendIndex, curationName, templateName, shouldSkipSendNameUpdate]);

    useEffect(() => {
        if (!currentSendIsEmail) return;

        // A/B Testing Flow
        if (isABTestingContent) {
            if (isEmailA) {
                // For Content A/B Testing: Email-A → Email-B content selection
                setNextStep(CustomCampaignSteps.SELECT_CONTENT);
            } else if (isEmailB) {
                // For Content A/B Testing: Email-B → Schedule (both emails)
                setNextStep(CustomCampaignSteps.SCHEDULE_CURATION);
            }
        } else if (isABTestingSendTime) {
            if (isEmailA) {
                // For Send Time A/B Testing: Email-A → Email-B schedule selection
                setNextStep(CustomCampaignSteps.SELECT_CONTENT);
            } else if (isEmailB) {
                // For Send Time A/B Testing: Email-B → Publish
                setNextStep(CustomCampaignSteps.PUBLISH_CAMPAIGN);
            }
        } else {
            // Normal flow (no A/B testing)
            if (currentSend.splitType === SplitTypes.MEETS) {
                setNextStep(CustomCampaignSteps.SELECT_PATH_ACTION);
            } else {
                setNextStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEmailA, isEmailB, isABTestingContent, isABTestingSendTime, setNextStep]);

    useEffect(() => {
        const isEnabled = hasCompleteEmailContent({ templateId, senderProfileId, subjectLine, preHeader });

        // Disable continue if there's a name error (duplicate or reserved name)
        if (hasNameError) {
            setIsContinueEnabled(false);
            return;
        }

        // For A/B content testing, add validation to ensure Email-A and Email-B are different
        // Only validate when user is on Email-B step (activeSendIndex === 1)
        if (
            isABTestingContent &&
            isEnabled &&
            activeSendIndex === 1 &&
            campaignState.sends[0] &&
            campaignState.sends[1]
        ) {
            const emailA = campaignState.sends[0] as any;
            const emailB = campaignState.sends[1] as any;

            // Check if both emails have content selected
            const emailAHasContent = hasCompleteEmailContent(emailA);
            const emailBHasContent = hasCompleteEmailContent(emailB);

            // If both have content, check if they're identical
            if (emailAHasContent && emailBHasContent) {
                // Disable continue if content is identical
                if (areEmailContentsIdentical(emailA, emailB)) {
                    setIsContinueEnabled(false);
                    return;
                }
            }
        }

        setIsContinueEnabled(isEnabled);
    }, [
        preHeader,
        senderProfileId,
        setIsContinueEnabled,
        subjectLine,
        templateId,
        isABTestingContent,
        campaignState.sends,
        activeSendIndex,
        hasNameError,
    ]);

    // Helper to generate send name for A/B testing or default format
    const generateSendName = useCallback(
        (sendIndex: number, contentName?: string) => {
            const curationPart = curationName || '';

            if (isABTestingContent) {
                const suffix = sendIndex === 0 ? 'Content A' : 'Content B';
                return `${curationPart} - ${suffix}`;
            }

            if (isABTestingSendTime) {
                const suffix = sendIndex === 0 ? 'Sendtime A' : 'Sendtime B';
                return `${curationPart} - ${suffix}`;
            }

            // Non-A/B testing: Use original format with content name and UUID
            const contentPart = contentName || '';
            return `${curationPart} - ${contentPart}.${uuidv4()}`;
        },
        [curationName, isABTestingContent, isABTestingSendTime]
    );

    const handleContentChange = useCallback(
        (contentId: number | null) => {
            if (!contentId) {
                dispatchCampaignAction(setTemplateId(null, activeSendIndex));
                // Only reset sender profile, subject line, and preview text if not editing a paused curation
                if (!isEditingPausedCampaign) {
                    dispatchCampaignAction(setSenderProfileId(null, activeSendIndex));
                    dispatchCampaignAction(setSubjectLine('', activeSendIndex));
                    dispatchCampaignAction(setPreHeader('', activeSendIndex));
                    // Reset send name to default (only for new curations NOT for A/B testing)
                    if (!shouldSkipSendNameUpdate && !isABTesting) {
                        const defaultName = generateSendName(activeSendIndex, '');
                        dispatchCampaignAction(setSendName(defaultName, activeSendIndex));
                        setCustomName(defaultName);
                    }
                }

                setContentPreviewId(null);
                return;
            }

            const newSelectedTemplate = templates.find((template) => template.id === contentId);
            if (!newSelectedTemplate) return;

            dispatchCampaignAction(setTemplateId(contentId, activeSendIndex));

            // For paused curations: override with new template values if they exist
            // For other curations: always set from template
            if (isEditingPausedCampaign) {
                // Override with new template values if they exist
                if (newSelectedTemplate.senderProfileId) {
                    dispatchCampaignAction(setSenderProfileId(newSelectedTemplate.senderProfileId, activeSendIndex));
                }
                if (newSelectedTemplate.subjectLine) {
                    dispatchCampaignAction(setSubjectLine(newSelectedTemplate.subjectLine, activeSendIndex));
                }
                if (newSelectedTemplate.preHeader) {
                    dispatchCampaignAction(setPreHeader(newSelectedTemplate.preHeader, activeSendIndex));
                }
            } else {
                // For non-paused curations: always set from template
                dispatchCampaignAction(setSenderProfileId(newSelectedTemplate.senderProfileId, activeSendIndex));
                dispatchCampaignAction(setSubjectLine(newSelectedTemplate.subjectLine ?? '', activeSendIndex));
                dispatchCampaignAction(setPreHeader(newSelectedTemplate.preHeader ?? '', activeSendIndex));

                // Reset send name to default with content name (only for new curations but not for A/B testing)
                if (!shouldSkipSendNameUpdate && !isABTesting) {
                    const newDefaultName = generateSendName(activeSendIndex, newSelectedTemplate.name);
                    dispatchCampaignAction(setSendName(newDefaultName, activeSendIndex));
                    setCustomName(newDefaultName);
                }
            }

            // Auto-copy feature: If selecting content for Email-A in A/B content testing,
            // and Email-B is empty, copy the templateId to Email-B
            // (Other fields are handled by AdditionalContentFields component)
            if (isABTestingContent && activeSendIndex === 0 && campaignState.sends[1]) {
                const emailB = campaignState.sends[1] as any;
                const isEmailBEmpty = !emailB.templateId;

                if (isEmailBEmpty) {
                    dispatchCampaignAction(setTemplateId(contentId, 1));
                    // Also set the default name for Email-B (A/B testing always updates names)
                    const emailBDefaultName = generateSendName(1, newSelectedTemplate.name);
                    dispatchCampaignAction(setSendName(emailBDefaultName, 1));
                }
            }

            if (contentPreviewId) {
                setContentPreviewId(contentId);
            }
        },
        [
            activeSendIndex,
            contentPreviewId,
            dispatchCampaignAction,
            setContentPreviewId,
            templates,
            isABTestingContent,
            campaignState.sends,
            isEditingPausedCampaign,
            shouldSkipSendNameUpdate,
            generateSendName,
            isABTesting,
        ]
    );

    const handlePreviewChange = useCallback(() => {
        setContentPreviewId(contentPreviewId ? null : templateId);
    }, [contentPreviewId, setContentPreviewId, templateId]);

    // Handlers for editing email name
    const handleEditClick = () => {
        setIsEditingName(true);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomName(event.target.value);
    };

    const handleNameSave = () => {
        // Don't save if name is duplicate or reserved
        if (hasNameError) {
            return;
        }

        setIsEditingName(false);
        // Dispatch action to save customName to campaignState
        dispatchCampaignAction(setSendName(customName, activeSendIndex));
    };

    const handleNameKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleNameSave();
        }
    };

    const getGuidingText = () => {
        if (isEmailA) {
            return GUIDING_TEXT.EMAIL_A;
        }
        if (isEmailB) {
            return GUIDING_TEXT.EMAIL_B;
        }
        // Normal flow
        return activeSendIndex === 0 ? GUIDING_TEXT.FIRST_EMAIL : GUIDING_TEXT.SUBSEQUENT_EMAIL;
    };

    const getHeaderText = () => {
        if (isABTestingContent || isABTestingSendTime) {
            return HEADER_TEXT.SELECT_CONTENT;
        }
        return HEADER_TEXT.SELECT_CONTENT;
    };

    const getBadgeContent = () => {
        if (isEmailA) {
            return BADGE_CONTENT.EMAIL_A;
        }
        if (isEmailB) {
            return BADGE_CONTENT.EMAIL_B;
        }
        return null;
    };

    if (!currentSendIsEmail) return null;

    const badgeContent = getBadgeContent();

    return (
        <FormLayout header={getHeaderText()} stepName="email" icon={<MailOutline />} guidingText={getGuidingText()}>
            <Stack rowGap={4}>
                {/* Editable send name section - only shown when feature flag is enabled */}
                {editSendnameEnabled && (
                    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                        <Stack direction="row" alignItems="flex-start" spacing={2}>
                            {badgeContent && <ABTestLabelBox label={badgeContent.badgeLabel} size="medium" />}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
                                {isEditingName ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <TextField
                                                    value={customName}
                                                    onChange={handleNameChange}
                                                    onKeyPress={handleNameKeyPress}
                                                    onBlur={handleNameSave}
                                                    autoFocus
                                                    multiline
                                                    variant="standard"
                                                    error={hasNameError}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '1.25rem',
                                                            fontWeight: 'bold',
                                                            wordBreak: 'break-word',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={handleNameSave}
                                                disabled={hasNameError}
                                                sx={{ padding: 0.5, mt: 0.5, flexShrink: 0 }}
                                            >
                                                <Check fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        {hasNameError && (
                                            <Typography variant="caption" color="error" fontSize={12}>
                                                {nameErrorMessage}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                {customName}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={handleEditClick}
                                                sx={{ padding: 0.5, flexShrink: 0 }}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        {hasNameError && (
                                            <Typography variant="caption" color="error" fontSize={12}>
                                                {nameErrorMessage}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    </Box>
                )}
                <ContentSelector
                    contentType={CampaignChannels.EMAIL}
                    contentId={templateId}
                    contentList={templates}
                    onContentChange={handleContentChange}
                    isPreviewVisible={!!contentPreviewId}
                    onPreviewChange={handlePreviewChange}
                    isLoading={isLoadingTemplates}
                    onContentUnavailableChange={onContentUnavailableChange}
                />
                <AdditionalContentFields
                    activeSendIndex={activeSendIndex}
                    currentSend={currentSend}
                    dispatchCampaignAction={dispatchCampaignAction}
                    isABTestingContent={isABTestingContent}
                    campaignSends={campaignState.sends}
                />
            </Stack>
        </FormLayout>
    );
};

export default EmailContentStep;
